import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from '../services/api';

const ShortlistContext = createContext();

export const useShortlist = () => {
  const context = useContext(ShortlistContext);
  if (!context) throw new Error('useShortlist must be used within ShortlistProvider');
  return context;
};

export const ShortlistProvider = ({ children }) => {
  const [shortlistedCarIds, setShortlistedCarIds] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadShortlistedIds = useCallback(async () => {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) { setLoading(false); return; }
      // Use bulk endpoint — ONE call, not per-car
      const res = await api.get('/shortlists/car_ids');
      setShortlistedCarIds(res.data?.data || []);
    } catch (error) {
      console.error('Error loading shortlisted IDs:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadShortlistedIds();
  }, [loadShortlistedIds]);

  const addToShortlist = async (carId) => {
    try {
      const res = await api.post('/shortlists', { car_id: carId });
      if (res.data?.success) {
        setShortlistedCarIds(prev => [...new Set([...prev, carId])]);
        return true;
      }
      return false;
    } catch (error) {
      console.error('Error adding to shortlist:', error);
      return false;
    }
  };

  const removeFromShortlist = async (carId) => {
    try {
      const res = await api.delete(`/shortlists/${carId}`);
      if (res.data?.success) {
        setShortlistedCarIds(prev => prev.filter(id => id !== carId));
        return true;
      }
      return false;
    } catch (error) {
      console.error('Error removing from shortlist:', error);
      return false;
    }
  };

  const isShortlisted = useCallback((carId) => {
    return shortlistedCarIds.includes(Number(carId));
  }, [shortlistedCarIds]);

  return (
    <ShortlistContext.Provider value={{
      shortlistedCarIds,
      shortlistCount: shortlistedCarIds.length,
      loading,
      addToShortlist,
      removeFromShortlist,
      isShortlisted,
      refreshShortlist: loadShortlistedIds,
    }}>
      {children}
    </ShortlistContext.Provider>
  );
};